import React, { Dispatch, memo, SetStateAction, useContext } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import moment from 'moment-timezone'
import EditIcon from 'transfix-icons/build/EditIcon'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import { Avatar } from 'transfix-ui/components/Avatar'
import { IconButton } from 'transfix-ui/components/IconButton'
import { TableCell, TableRow } from 'transfix-ui/components/Table'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Box, makeStyles } from 'transfix-ui/core'
import { $enum } from 'ts-enum-util'
import { IPricingTypes } from 'types/graphqlTypes'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import {
  formatMiles,
  formatPostalCode,
  formatPricingProgram,
} from 'apps/marketplace/utils/helpers/formatShipperLaneRow'
import { IShipperRate, RATE_TYPE_DISPLAY, SHIPPER_RATES_COLUMNS } from 'apps/rms/types/shipperRates'
import { openEditShipperRateModal } from 'apps/rms/utils/actions/shipperRates'
import { IDropdownOption } from 'components/forms'
import { AddIcon, EllipsisIcon, TrueRatePlusFilledIcon } from 'transfix-icons'
import { OverflowMenu, useOverflowToggle } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'
import { TMS_EQUIPMENT as EQUIPMENT_TYPE } from 'constants/tmsEquipment'

interface IShipperRatesTableProps {
  shipperRate: IShipperRate
  openAcceptanceCapModal: () => void
  setShipperRate: Dispatch<SetStateAction<IShipperRate | undefined>>
}

const useStyles = makeStyles({
  option: {
    display: 'flex',
    alignItems: 'center',
  },
})

export const ShipperRatesTableRow = memo<IShipperRatesTableProps>(
  ({ openAcceptanceCapModal, setShipperRate, shipperRate }) => {
    const dispatch = useDispatch()
    const { getFlag } = useContext(FeatureFlagsContext)

    const { enabled: mpRmsRepricedRates } = getFlag('mpRmsRepricedRates')
    const history = useHistory()

    const {
      awardType,
      delivery: {
        city: deliveryCity,
        postalCode: deliveryPostalCode,
        postalCode3: deliveryPostalCode3,
        stateCode: deliveryStateCode,
      },
      endDate,
      equipment: { key: equipmentKey },
      id,
      isDeliveryDropTrailer,
      isPickupDropTrailer,
      isRepricedRate,
      isZonePresent,
      minChargeRate,
      pickup: {
        city: pickupCity,
        postalCode: pickupPostalCode,
        postalCode3: pickupPostalCode3,
        stateCode: pickupStateCode,
      },
      pricingProgram,
      pricingType,
      rate,
      shipperApplication,
      shipperLaneId,
      shipperMiles,
      startDate,
      volume,
      volumeType,
    } = shipperRate || {}
    const { amount: rateAmount, rateTypeKey: rateType } = rate || {}
    const { name: shipperName, opsRep } = shipperApplication || {}
    const { amount: minCharge } = minChargeRate || {}
    const { firstName = '', lastName = '' } = opsRep || {}
    const { dateFormatShort } = FORMATS
    const program = pricingProgram ? pricingProgram.program : undefined
    const pickupDrop = isPickupDropTrailer ? 'Drop' : 'Live'
    const deliveryDrop = isDeliveryDropTrailer ? 'Drop' : 'Live'

    const openEditModal = () => {
      dispatch(openEditShipperRateModal(shipperRate))
    }

    const classes = useStyles()

    const editRateOption = {
      label: 'Edit Rate',
      itemComponent: (
        <Box className={classes.option}>
          <EditIcon size='large' data-test={`edit-shipper-rate-${id}`} />
          <Box component='span' ml={2}>
            Edit Rate
          </Box>
        </Box>
      ),
      action: () => openEditModal(),
    }

    const updateAcceptancCapOption = {
      label: shipperRate.acceptanceCap ? 'Edit Acceptance Cap' : 'Set Acceptance Cap',
      itemComponent: (
        <Box className={classes.option}>
          {shipperRate.acceptanceCap ? <EditIcon size='large' /> : <AddIcon size='large' />}
          <Box component='span' ml={2}>
            {shipperRate.acceptanceCap ? 'Edit Acceptance Cap' : 'Set Acceptance Cap'}
          </Box>
        </Box>
      ),
      action: () => {
        setShipperRate(shipperRate)
        openAcceptanceCapModal()
      },
    }

    const options: IDropdownOption[] = [editRateOption, updateAcceptancCapOption]

    const { clickOverflowTrigger, expanded, setExpanded } = useOverflowToggle()

    const tableCells = [
      { label: 'id', value: id || DISPLAY.empty },
      { label: 'shipperLaneId', value: shipperLaneId || DISPLAY.empty },
      {
        label: SHIPPER_RATES_COLUMNS.isRepricedRate,
        value: isRepricedRate ? 'Yes' : DISPLAY.empty,
      },
      { label: 'shipper', value: shipperName || DISPLAY.empty },
      {
        label: 'equipment',
        value: $enum(EQUIPMENT_TYPE).getValueOrDefault(equipmentKey, DISPLAY.empty),
      },
      {
        label: 'startDate',
        value: startDate ? moment(startDate).format(dateFormatShort) : DISPLAY.empty,
      },
      {
        label: 'endDate',
        value: endDate ? moment(endDate).format(dateFormatShort) : DISPLAY.empty,
      },
      { label: 'awardType', value: awardType ? capitalize(awardType) : DISPLAY.empty },
      {
        label: 'pickupCity',
        value: pickupCity ? `${capitalize(pickupCity)}, ${pickupStateCode}` : DISPLAY.empty,
      },
      { label: 'pickupZipcode', value: formatPostalCode(pickupPostalCode, pickupPostalCode3) },
      {
        label: 'deliveryCity',
        value: deliveryCity ? `${capitalize(deliveryCity)}, ${deliveryStateCode}` : DISPLAY.empty,
      },
      {
        label: 'deliveryZipcode',
        value: formatPostalCode(deliveryPostalCode, deliveryPostalCode3),
      },
      { label: 'shipperMiles', value: formatMiles(Number(shipperMiles)) },
      {
        label: 'pricingProgram',
        pricingType,
        value: formatPricingProgram(pricingType, program),
      },
      {
        label: 'rate',
        rateType: $enum(RATE_TYPE_DISPLAY).getValueOrDefault(rateType, undefined),
        value: rateAmount || DISPLAY.empty,
      },
      { label: 'minCharge', value: minCharge || DISPLAY.empty },
      { label: 'volume', value: volume || DISPLAY.empty },
      { label: 'volumeType', value: volumeType ? capitalize(volumeType) : DISPLAY.empty },
      { label: 'dropType', value: `${pickupDrop} - ${deliveryDrop}` },
    ]

    const onShipperRateRowClick = () => {
      history.push(`/${id}`)
    }

    return (
      <TableRow dataTest={`shipper-rate-${id}`} highlightOnHover onClick={onShipperRateRowClick}>
        {tableCells
          .filter(cell => cell.label !== SHIPPER_RATES_COLUMNS.isRepricedRate || mpRmsRepricedRates)
          .map(cell => (
            <TableCell dataTest={`shipper-rate-${id}-${cell.label}`} key={`{id}-${cell.label}`}>
              {cell.value}
              {cell.label === 'rate' && cell.rateType && (
                <p className='text-small'>{cell.rateType}</p>
              )}
              {cell.label === 'pricingProgram' && cell.pricingType === IPricingTypes.variable && (
                <Box display='flex' flexDirection='row' alignItems='center'>
                  <TrueRatePlusFilledIcon size='large' color='coolGray8' />
                </Box>
              )}
              {cell.label === 'id' && isZonePresent && (
                <Box ml={1.25} display='flex' alignItems='center'>
                  <Tooltip position='top' id='contract' trigger={<InfoIcon />}>
                    Zone Priced
                  </Tooltip>
                </Box>
              )}
            </TableCell>
          ))}
        <TableCell colSpan={2} dataTest={`shipper-rate-${id}-accountManager`}>
          {firstName && <Avatar name={`${firstName} ${lastName}`} />}
        </TableCell>
        <TableCell type='icon'>
          <OverflowMenu
            options={options}
            trigger={
              <IconButton
                color='tertiary'
                label='overflow-trigger'
                Icon={EllipsisIcon}
                onClick={clickOverflowTrigger}
                data-test={`shipper-rate-overflow-menu-${id}`}
                size='large'
              />
            }
            expanded={expanded}
            onExpanded={setExpanded}
            dropdownId='shipper-rate-actions'
          />
        </TableCell>
      </TableRow>
    )
  }
)
